import type { Sample } from '@ctcells/contract'
import { maskPii, t } from '@ctcells/i18n'

/** 보기 줄의 글자 — 비었으면 `''`(그 줄을 그리지 않는다). 숫자 `0` 은 값이다 */
function valueText(value: string | number | null | undefined): string {
  return value == null ? '' : String(value).trim()
}

/**
 * 「시료」 카드 머리 두 줄(환자 번호 · 종류) — 값 있는 줄만. 모니터링 시료 서랍이 묶음의 다른 시료 카드에도 같은 함수를 쓴다.
 */
export function sampleIdentityHeadRows(sample: Sample, visible: boolean) {
  return [
    {
      key: 'patient',
      label: t('column.patientId'),
      value: valueText(sample.patient_id) === '' ? '' : maskPii(sample.patient_id, visible),
    },
    { key: 'sampleType', label: t('field.sampleType'), value: valueText(sample.sample_type) },
  ].filter((row) => row.value !== '')
}

/**
 * **검사 정보** — 시료 추가 창(`SampleNewScreen`)이 받는 선택 입력 칸 가운데 **서버 응답에 돌아오는 것만**, 그 창의 이름 · 차례로.
 *
 * | 시료 추가 창 | 응답 칸 | 여기 |
 * | --- | --- | --- |
 * | 채혈 일시(날짜 + 시 · 분) | `blood_draw_date`(날짜) · 시각은 `metadata.blood_draw_at` | 날짜만 — 🔴 `metadata` 가 응답에 없다(BR-59 · 2026-10-23 실측 500건 0건) |
 * | 암종 | `cancer_type` | ✓ |
 * | 암 병기 | `cancer_stage` | ✓ |
 * | 추가 입력(이름 · 값) | `metadata` | ✗ — 응답에 없다(BR-59). 값이 오기 시작하면 이 자리에 키 · 값으로 더한다 |
 *
 * 🔴 **적혈구 농도 줄은 없다**(eric 2026-10-04 「적혈구 이건 필수 아니고.. metadata 들어갈 거야」 → 2026-10-23 「왜 적혈구 농도가 있어?」).
 *    시료 추가 창에서 그 칸을 걷었는데 보이는 쪽에만 남아 있었다. 계약 `Sample.rbc_concentration` 은 응답에 남는다(옛 시료 ·
 *    테스트 작업 빠른 등록이 아직 싣는다) — 화면만 안 보인다. 되살리지 마라.
 * 🔴 **시료 목록의 서랍은 빈 칸을 「NA」 로 적는다**(2026-10-01 CTCells 회의 「빈칸은 NA 처리 · 검사 진행 가능」) — 부르는 쪽이 정한다.
 *    모니터링 시료 서랍은 값 있는 줄만 · 작업 상세 펼침은 `—`.
 */
export function sampleTestInfoRows(sample: Sample) {
  return [
    { key: 'drawDate', label: t('field.drawAt'), value: valueText(sample.blood_draw_date) },
    { key: 'cancerType', label: t('field.cancerType'), value: valueText(sample.cancer_type) },
    { key: 'cancerStage', label: t('field.cancerStage'), value: valueText(sample.cancer_stage) },
  ]
}
